<template>
  <div id="app">
    <!-- 页面加载指示器 -->
    <div class="page-loader" v-if="isLoading">
      <div class="loader-spinner"></div>
    </div>
    
    <!-- 登录过期提示 -->
    <div class="session-expired-alert" v-if="showSessionExpired">
      <div class="alert-content">
        <span>登录已过期，请重新登录</span>
        <button @click="closeSessionExpiredAlert">关闭</button>
      </div>
    </div>
    
    <Header />
    <router-view v-slot="{ Component }">
      <transition>
        <component :is="Component" />
      </transition>
    </router-view>
    <Footer />
    
    <!-- 回到顶部按钮 -->
    <div class="back-to-top" v-show="showBackToTop" @click="scrollToTop">
      <i class="arrow-up">↑</i>
    </div>
  </div>
</template>

<script>
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'

export default {
  name: 'AppView',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      showBackToTop: false,
      isLoading: false,
      showSessionExpired: false,
      tokenCheckInterval: null
    }
  },
  created() {
    // 初始化应用时检查令牌状态
    this.initTokenCheck()
    
    // 检查URL参数是否包含过期标记
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('expired') === 'true') {
      this.showSessionExpired = true
      // 移除URL中的过期参数
      const newUrl = window.location.pathname + window.location.hash
      window.history.replaceState({}, document.title, newUrl)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    
    // 设置定期检查令牌状态的定时器
    this.tokenCheckInterval = setInterval(this.checkTokenStatus, 60000) // 每分钟检查一次
  },
  // 将 beforeDestroy 改为 beforeUnmount
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    
    // 清除定时器
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval)
    }
  },
  methods: {
    handleScroll() {
      this.showBackToTop = window.scrollY > 300
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    // 添加缺少的方法
    handleRouteChange() {
      // 路由变化处理逻辑
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 300)
    },
    // 初始化令牌检查
    initTokenCheck() {
      this.$store.dispatch('initApp') // 修改为调用initAuth而不是initApp
    },
// 定期检查令牌状态
checkTokenStatus() {
  // 检查令牌是否过期
  const tokenExpiry = localStorage.getItem('tokenExpiresAt')
  const isAuthenticated = !!localStorage.getItem('token')
  
  if (isAuthenticated && tokenExpiry) {
    const isExpired = Date.now() > parseInt(tokenExpiry)
    
    if (isExpired) {
      // 令牌已过期，登出用户并显示提示
      this.$store.dispatch('logout')
      this.showSessionExpired = true
      
      // 如果当前页面需要认证，则重定向到首页
      const currentRoute = this.$router.currentRoute.value
      if (currentRoute.meta.requiresAuth) {
        this.$router.push('/')
      }
    }
  }
},
    // 关闭登录过期提示
    closeSessionExpiredAlert() {
      this.showSessionExpired = false
    }
  }
}
</script>

<style>
@import '@/assets/css/css.css';
@import '@/assets/css/styles.css';
@import '@/assets/css/ds.css';
@import '@/assets/css/responsive.css'; /* 新增响应式样式 */
@import '@/assets/css/global.css'; /* 添加全局样式 */
/* 页面过渡动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 页面加载指示器样式 */
.page-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--primary-color, #4285f4);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 登录过期提示样式 */
.session-expired-alert {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 10px 15px;
  z-index: 10000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.3s ease-out;
}

.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.alert-content button {
  background-color: #721c24;
  color: white;
  border: none;
  border-radius: 3px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 12px;
}

.alert-content button:hover {
  background-color: #5a171d;
}

@keyframes slideDown {
  from {
    transform: translate(-50%, -20px);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

/* 回到顶部按钮样式 */
.back-to-top {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 40px;
  height: 40px;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s ease, transform 0.3s ease;
  z-index: 1000;
}

.back-to-top:hover {
  background-color: var(--secondary-color);
  transform: translateY(-3px);
}

.arrow-up {
  font-style: normal;
  font-size: 20px;
}

@media screen and (max-width: 768px) {
  .back-to-top {
    bottom: 20px;
    right: 20px;
    width: 35px;
    height: 35px;
  }
  
  .session-expired-alert {
    width: 90%;
    max-width: 300px;
  }
}
</style>